import { AnimatePresence, motion } from "framer-motion";
import {
  BarChart3,
  CalendarDays,
  ChevronLeft,
  CreditCard,
  Download,
  LayoutDashboard,
  ListFilter,
  Menu,
  PieChart,
  PiggyBank,
  Settings,
  TrendingUp,
  Wallet,
  X,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { cn } from "@repo/core";
import type { ViewId } from "../App";
import { useAvailableYears, usePeopleOptions } from "../hooks/useBudgetData";
import { personLabel } from "../i18n/labels";
import { useT } from "../i18n/useT";
import { PrintChrome } from "./PrintChrome";

interface NavGroupDef {
  groupKey: string;
  items: { id: ViewId; labelKey: string; icon: React.ElementType }[];
}

const NAV_GROUPS: NavGroupDef[] = [
  {
    groupKey: "nav.group.setup",
    items: [
      { id: "dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard },
      { id: "monthly", labelKey: "nav.monthly", icon: Wallet },
    ],
  },
  {
    groupKey: "nav.group.daily",
    items: [
      { id: "transactions", labelKey: "nav.transactions", icon: ListFilter },
      { id: "calendar", labelKey: "nav.calendar", icon: CalendarDays },
    ],
  },
  {
    groupKey: "nav.group.where",
    items: [
      { id: "rule503020", labelKey: "nav.rule503020", icon: PieChart },
      { id: "networth", labelKey: "nav.networth", icon: TrendingUp },
    ],
  },
  {
    groupKey: "nav.group.wealth",
    items: [
      { id: "debt", labelKey: "nav.debt", icon: CreditCard },
      { id: "sinking", labelKey: "nav.sinking", icon: PiggyBank },
    ],
  },
];

const BOTTOM_NAV = [
  { id: "dashboard" as ViewId, labelKey: "nav.bottom.home", icon: LayoutDashboard },
  { id: "transactions" as ViewId, labelKey: "nav.bottom.log", icon: ListFilter },
  { id: "monthly" as ViewId, labelKey: "nav.bottom.budget", icon: BarChart3 },
  { id: "debt" as ViewId, labelKey: "nav.bottom.debt", icon: CreditCard },
  { id: "networth" as ViewId, labelKey: "nav.bottom.wealth", icon: TrendingUp },
];

interface LayoutProps {
  activeView: ViewId;
  onViewChange: (v: ViewId) => void;
  person: string;
  onPersonChange: (p: string) => void;
  year: number;
  onYearChange: (y: number) => void;
  onExport?: () => void;
  children: React.ReactNode;
  /** Extra bottom padding so the floating + button never covers page content */
  reserveFabSpace?: boolean;
}

function NavItem({
  item,
  isActive,
  onClick,
  collapsed,
}: {
  item: { id: ViewId; label: string; icon: React.ElementType };
  isActive: boolean;
  onClick: () => void;
  collapsed: boolean;
}) {
  const Icon = item.icon;
  return (
    <button
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      className={cn(
        "relative w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors group",
        collapsed && "justify-center px-0",
        isActive
          ? "bg-sidebar-accent text-sidebar-accent-foreground"
          : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
      )}
    >
      {isActive && (
        <motion.div
          layoutId="sidebar-indicator"
          className="absolute inset-0 rounded-lg bg-sidebar-accent"
          transition={{ type: "spring", bounce: 0.15, duration: 0.35 }}
        />
      )}
      <Icon
        size={16}
        className={cn(
          "relative z-10 shrink-0",
          isActive ? "text-primary" : "text-sidebar-foreground/50 group-hover:text-sidebar-foreground/80"
        )}
      />
      <AnimatePresence>
        {!collapsed && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="relative z-10 text-left truncate"
          >
            {item.label}
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
}

export function Layout({
  activeView,
  onViewChange,
  person,
  onPersonChange,
  year,
  onYearChange,
  onExport,
  children,
  reserveFabSpace = false,
}: LayoutProps) {
  const { t } = useT();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const people = usePeopleOptions();
  const years = useAvailableYears();

  const navGroups = useMemo(
    () =>
      NAV_GROUPS.map((g) => ({
        label: t(g.groupKey),
        items: g.items.map((i) => ({ id: i.id, label: t(i.labelKey), icon: i.icon })),
      })),
    [t]
  );

  const bottomNav = useMemo(
    () => BOTTOM_NAV.map((i) => ({ id: i.id, label: t(i.labelKey), icon: i.icon })),
    [t]
  );

  const allNavItems = navGroups.flatMap((g) => g.items);
  const activeLabel =
    allNavItems.find((i) => i.id === activeView)?.label ??
    (activeView === "settings" ? t("nav.settings") : t("layout.brand"));

  const peopleOptions = people.map((p) =>
    p === "All"
      ? { value: "All", label: t("common.all") }
      : { value: p, label: personLabel(p, t) }
  );

  const SidebarContent = ({ onClose }: { onClose?: () => void }) => (
    <>
      <div className={cn("flex items-center gap-2.5 h-14 px-4 border-b border-sidebar-border shrink-0", collapsed && !onClose && "justify-center px-0")}>
        <div className="w-7 h-7 rounded-lg bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">
          $
        </div>
        <AnimatePresence>
          {(!collapsed || onClose) && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "auto" }}
              exit={{ opacity: 0, width: 0 }}
              className="overflow-hidden"
            >
              <p className="font-semibold text-sm text-sidebar-foreground leading-none whitespace-nowrap">{t("layout.brand")}</p>
              <p className="text-[10px] text-sidebar-foreground/40 whitespace-nowrap">{t("layout.tagline")}</p>
            </motion.div>
          )}
        </AnimatePresence>
        {onClose && (
          <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-sidebar-accent/50 text-sidebar-foreground/60">
            <X size={15} />
          </button>
        )}
      </div>

      {(!collapsed || onClose) && (
        <div className="px-3 py-3 border-b border-sidebar-border space-y-2.5 shrink-0">
          <div>
            <label className="text-[9px] font-semibold uppercase tracking-widest text-sidebar-foreground/50 block mb-1">
              {t("common.year")}
            </label>
            <div className="relative">
              <select
                value={year}
                onChange={(e) => onYearChange(Number(e.target.value))}
                className="w-full app-input text-xs cursor-pointer font-medium transition-all"
                style={{
                  backgroundColor: "hsl(var(--sidebar-accent))",
                  color: "hsl(var(--sidebar-foreground))",
                  borderColor: "hsl(var(--sidebar-border))",
                }}
              >
                {years.map((y) => (
                  <option key={y} value={y} className="bg-card text-foreground">
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[9px] font-semibold uppercase tracking-widest text-sidebar-foreground/50 block mb-1">
              {t("common.person")}
            </label>
            <div className="relative">
              <select
                value={person}
                onChange={(e) => onPersonChange(e.target.value)}
                className="w-full app-input text-xs cursor-pointer font-medium transition-all"
                style={{
                  backgroundColor: "hsl(var(--sidebar-accent))",
                  color: "hsl(var(--sidebar-foreground))",
                  borderColor: "hsl(var(--sidebar-border))",
                }}
              >
                {peopleOptions.map((p) => (
                  <option key={p.value} value={p.value} className="bg-card text-foreground">
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {navGroups.map((group) => (
          <div key={group.label}>
            <AnimatePresence>
              {(!collapsed || onClose) && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-[9px] font-semibold uppercase tracking-widest text-sidebar-foreground/30 px-3 mb-1"
                >
                  {group.label}
                </motion.p>
              )}
            </AnimatePresence>
            {group.items.map((item) => (
              <NavItem
                key={item.id}
                item={item}
                isActive={activeView === item.id}
                collapsed={collapsed && !onClose}
                onClick={() => {
                  onViewChange(item.id);
                  onClose?.();
                }}
              />
            ))}
          </div>
        ))}
      </nav>

      <div className="shrink-0 border-t border-sidebar-border p-3 space-y-1.5">
        <NavItem
          item={{ id: "settings" as ViewId, label: t("nav.settings"), icon: Settings }}
          isActive={activeView === "settings"}
          collapsed={collapsed && !onClose}
          onClick={() => {
            onViewChange("settings" as ViewId);
            onClose?.();
          }}
        />

        {onExport && (!collapsed || onClose) && (
          <button
            onClick={onExport}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-sidebar-border text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
          >
            <Download size={12} />
            {t("common.export")}
          </button>
        )}
        {!onClose && (
          <button
            onClick={() => setCollapsed((v) => !v)}
            className="w-full flex items-center justify-center gap-1.5 py-1 text-xs text-sidebar-foreground/40 hover:text-sidebar-foreground transition-colors"
          >
            <motion.div animate={{ rotate: collapsed ? 180 : 0 }} transition={{ duration: 0.25 }}>
              <ChevronLeft size={14} />
            </motion.div>
            {!collapsed && <span>{t("layout.collapse")}</span>}
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className="flex h-full min-h-0 overflow-hidden bg-background">

      {/* ── Desktop sidebar ──────────────────────────── */}
      <aside
        className={cn(
          "hidden lg:flex flex-col border-r border-sidebar-border transition-all duration-300 shrink-0 no-print",
          collapsed ? "w-16" : "w-60"
        )}
        style={{ background: "var(--sidebar-gradient, hsl(var(--sidebar)))" }}
      >
        <SidebarContent />
      </aside>

      {/* ── Mobile sidebar overlay ───────────────────── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "spring", bounce: 0, duration: 0.28 }}
              className="fixed left-0 top-0 bottom-0 w-68 border-r border-sidebar-border z-50 lg:hidden flex flex-col"
              style={{ background: "var(--sidebar-gradient, hsl(var(--sidebar)))" }}
            >
              <SidebarContent onClose={() => setMobileOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ── Main area ────────────────────────────────── */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Header — content width matches page max */}
        <header className="shrink-0 z-10 no-print border-b border-border bg-background/95 backdrop-blur-sm">
          <div className="mx-auto w-full max-w-[1360px] flex items-center justify-between h-14 px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 -ml-1 rounded-lg hover:bg-accent transition-colors text-muted-foreground"
            >
              <Menu size={18} />
            </button>
            <div>
              <h1 className="font-semibold text-sm text-foreground leading-none">{activeLabel}</h1>
              <p className="text-[10px] text-muted-foreground mt-0.5 hidden sm:block">
                {year} · {person === "All" ? t("common.all") : personLabel(person, t)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative hidden sm:block lg:hidden">
              <select
                value={person}
                onChange={(e) => onPersonChange(e.target.value)}
                className="app-input text-xs w-32 py-1 cursor-pointer font-medium"
              >
                {peopleOptions.map((p) => (
                  <option key={p.value} value={p.value} className="bg-card text-foreground">
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
              {year}
            </div>
          </div>
          </div>
        </header>

        {/* Page — capped & centered on wide desktops (sidebar unchanged).
            Extra bottom padding when FAB is visible so content never sits under it. */}
        <main
          className={cn(
            "flex-1 overflow-y-auto",
            reserveFabSpace
              ? "pb-36 lg:pb-28" /* bottom nav + FAB clearance */
              : "pb-20 lg:pb-4"
          )}
        >
          <PrintChrome />
          <div className="mx-auto w-full max-w-[1360px]">
            <motion.div
              key={activeView}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18 }}
            >
              {children}
            </motion.div>
          </div>
        </main>

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 flex items-stretch h-16 bg-background/95 backdrop-blur-sm border-t border-border z-30 no-print">
          {bottomNav.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onViewChange(item.id)}
                className={cn(
                  "flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors relative",
                  isActive ? "text-primary" : "text-muted-foreground"
                )}
              >
                {isActive && (
                  <motion.div
                    layoutId="bottom-indicator"
                    className="absolute top-0 w-8 h-0.5 rounded-full bg-primary"
                    transition={{ type: "spring", bounce: 0.2 }}
                  />
                )}
                <Icon size={20} />
                {item.label}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
